// src/pages/providers/4_settings/components/ManageOrgConfigDialog.js
//
// Multi-step dialog for editing organisation details sections:
//   - Support Contacts (phone, email, website)
//   - Letterhead (address fields only - phone/email/website sync from Support Contacts)
//   - Branding (primary/secondary colour - partnershipText managed at platform level)
//
// Follows the same UX pattern as ProviderEditUserForm.js:
//   summary → edit form → review diff → confirm → success
//
// All changes go through a single updateOrgConfig Cloud Function that accepts
// a section name and data payload, avoiding the need for separate CFs per
// config section.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import {
  XIcon,
  ChevronRightIcon,
  ClockIcon,
} from '@heroicons/react/outline';
import {
  PhoneIcon,
  MailIcon,
  GlobeAltIcon,
  LocationMarkerIcon,
  ColorSwatchIcon,
  CheckCircleIcon as CheckCircleSolid,
} from '@heroicons/react/solid';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../../firebase';
import ConfigHistoryTimeline from './ConfigHistoryTimeline';

// ─── ThinkDivergent default brand colours (fallback when org hasn't customised) ──
const TD_DEFAULT_PRIMARY = '#43213e';
const TD_DEFAULT_SECONDARY = '#e7c22f';

// ─── Field definitions for each config section ──────────────────────────

const SUPPORT_CONTACTS_FIELDS = [
  { key: 'phone', label: 'Phone', placeholder: 'e.g. 020 1234 5678', icon: PhoneIcon },
  { key: 'email', label: 'Email', placeholder: 'e.g. support@example.com', icon: MailIcon, required: true },
  { key: 'website', label: 'Website', placeholder: 'e.g. https://example.com', icon: GlobeAltIcon },
];

// Phone, email and website are managed via Support Contacts and automatically
// mirrored into the letterhead on save (backend sync in updateOrgConfig CF).
// Only address fields are exposed here to avoid duplicate inputs.
const LETTERHEAD_FIELDS = [
  { key: 'addressLine1', label: 'Address Line 1', placeholder: 'e.g. 123 Main Street', required: true },
  { key: 'addressLine2', label: 'Address Line 2', placeholder: 'e.g. Hammersmith' },
  { key: 'addressLine3', label: 'Address Line 3', placeholder: 'e.g. London' },
  { key: 'addressLine4', label: 'Address Line 4 (Postcode)', placeholder: 'e.g. W14 8YG' },
];

// partnershipText is managed at the platform level (not exposed to org admins).
const BRANDING_FIELDS = [
  { key: 'primaryColor', label: 'Primary Brand Colour', type: 'color', placeholder: TD_DEFAULT_PRIMARY },
  { key: 'secondaryColor', label: 'Secondary Brand Colour', type: 'color', placeholder: TD_DEFAULT_SECONDARY },
];

// Section metadata for display and routing
const SECTIONS = {
  supportContacts: {
    label: 'Edit Support Contact Details',
    description: 'Contact information shown in patient and informant emails',
    icon: PhoneIcon,
    fields: SUPPORT_CONTACTS_FIELDS,
    cfSection: 'supportContacts',
  },
  letterhead: {
    label: 'Edit Letterhead Address',
    description: 'Address details used in generated documents',
    icon: LocationMarkerIcon,
    fields: LETTERHEAD_FIELDS,
    cfSection: 'letterhead',
  },
  branding: {
    label: 'Edit Email Branding',
    description: 'Brand colours used in emails notifications',
    icon: ColorSwatchIcon,
    fields: BRANDING_FIELDS,
    cfSection: 'branding',
  },
};

// Sections to pass to the history CF when viewing audit trail for this dialog
const HISTORY_SECTIONS = ['supportContacts', 'letterhead', 'branding'];

// ─── Helper: check if a value looks like a valid hex colour ──────────────
const isHexColor = (val) => /^#([0-9A-Fa-f]{3}){1,2}$/.test(val);

// ─── Helper: resolve display colour for a branding field ─────────────────
// Returns the org's custom colour if set, otherwise the TD default.
const resolveColor = (orgBranding, fieldKey) => {
  const custom = orgBranding?.[fieldKey];
  if (custom && isHexColor(custom)) return custom;
  if (fieldKey === 'primaryColor') return TD_DEFAULT_PRIMARY;
  if (fieldKey === 'secondaryColor') return TD_DEFAULT_SECONDARY;
  return '';
};

function ManageOrgConfigDialog({ isOpen, onClose, orgConfig, onConfigSaved }) {
  // orgConfig shape: { supportContacts: {...}, letterhead: {...}, branding: {...}, orgName: '' }

  // Navigation: 'summary' | 'edit-{sectionKey}' | 'confirm' | 'success'
  const [currentStep, setCurrentStep] = useState('summary');
  const [activeSection, setActiveSection] = useState(null);
  const [editData, setEditData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Ref for the inner content wrapper - used to animate height transitions
  const contentRef = useRef(null);
  const [animatedHeight, setAnimatedHeight] = useState('auto');

  // Measure the content element's natural height after each step change so
  // the modal container can animate smoothly between different content sizes.
  const measureAndAnimate = useCallback(() => {
    if (!contentRef.current) return;
    const el = contentRef.current;
    const newHeight = el.scrollHeight;
    setAnimatedHeight(newHeight + 'px');
  }, []);

  // Reset state when the dialog opens/closes or orgConfig changes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep('summary');
      setActiveSection(null);
      setEditData({});
      setIsSubmitting(false);
      setSubmitError('');
      setAnimatedHeight('auto');
    }
  }, [isOpen]);

  // Re-measure whenever the visible step changes
  useEffect(() => {
    // Small delay so the DOM renders the new step content first
    const timer = setTimeout(measureAndAnimate, 30);
    return () => clearTimeout(timer);
  }, [currentStep, measureAndAnimate]);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const getOriginalData = (sectionKey) => {
    if (!orgConfig) return {};
    return orgConfig[sectionKey] || {};
  };

  // For branding fields, the "original" value should include TD defaults so
  // that the edit form pre-populates with the effective colour, and the
  // confirm diff shows a meaningful "from" value instead of "(empty)".
  const getEffectiveOriginal = (sectionKey) => {
    const raw = getOriginalData(sectionKey);
    if (sectionKey !== 'branding') return raw;
    return {
      ...raw,
      primaryColor: resolveColor(raw, 'primaryColor'),
      secondaryColor: resolveColor(raw, 'secondaryColor'),
    };
  };

  const computeChangedFields = () => {
    if (!activeSection) return null;
    const original = getEffectiveOriginal(activeSection);
    const changes = {};

    const sectionDef = SECTIONS[activeSection];
    for (const field of sectionDef.fields) {
      const origVal = (original[field.key] || '').toString().trim();
      const newVal = (editData[field.key] || '').toString().trim();
      if (newVal !== origVal) {
        changes[field.key] = newVal;
      }
    }

    return Object.keys(changes).length > 0 ? changes : null;
  };

  // Build the full section data to send to the CF (includes all fields,
  // not just the changed ones, since the CF does a dot-notation merge)
  const buildSectionPayload = () => {
    const sectionDef = SECTIONS[activeSection];
    const payload = {};
    for (const field of sectionDef.fields) {
      payload[field.key] = (editData[field.key] || '').trim();
    }
    return payload;
  };

  // ─── Navigation ──────────────────────────────────────────────────────

  const goToEdit = (sectionKey) => {
    setActiveSection(sectionKey);
    const original = getEffectiveOriginal(sectionKey);
    const initialData = {};
    SECTIONS[sectionKey].fields.forEach((f) => {
      initialData[f.key] = original[f.key] || '';
    });
    setEditData(initialData);
    setSubmitError('');
    setCurrentStep(`edit-${sectionKey}`);
  };

  const goToConfirm = () => {
    if (computeChangedFields()) {
      setCurrentStep('confirm');
    }
  };

  const goBackToSummary = () => {
    setCurrentStep('summary');
    setActiveSection(null);
    setSubmitError('');
  };

  const goBackToEdit = () => {
    setCurrentStep(`edit-${activeSection}`);
    setSubmitError('');
  };

  // ─── Submit ──────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!activeSection) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const updateOrgConfig = httpsCallable(functions, 'updateOrgConfig');
      await updateOrgConfig({
        section: SECTIONS[activeSection].cfSection,
        data: buildSectionPayload(),
      });

      setCurrentStep('success');

      if (onConfigSaved) {
        onConfigSaved();
      }
    } catch (err) {
      console.error('Error updating org config:', err);
      setSubmitError(
        err?.message || 'An unexpected error occurred. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Render: Summary ─────────────────────────────────────────────────

  const renderSummary = () => (
    <div className="space-y-3">
      <h3 className="text-lg font-bold text-gray-900 pr-6">
        Organisation Details
      </h3>
      <p className="text-sm text-gray-500">
        Manage your organisation&apos;s support contact details, letterhead address, and email branding.
      </p>

      <div className="bg-white border border-gray-200 rounded-md p-3">
        <h4 className="text-xs font-medium text-gray-900 mb-2">Actions</h4>
        <div className="space-y-2">
          {Object.entries(SECTIONS).map(([sectionKey, section]) => {
            const data = getOriginalData(sectionKey);

            // Branding is always considered "configured" because all orgs
            // get TD default colours even before explicit customisation.
            

            const Icon = section.icon;

            return (
              <button
                key={sectionKey}
                onClick={() => goToEdit(sectionKey)}
                className="w-full flex items-center justify-between p-2 text-left border border-gray-200 rounded-md hover:bg-primary/5 hover:border-primary/30 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <Icon className="h-4 w-4 text-primary" />
                  <div>
                    <p className="text-xs font-semibold text-gray-900">
                      {section.label}
                    </p>
                    <p className="text-xs text-gray-500">
                      {section.description}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {/* Show colour swatches next to the branding row */}
                  {sectionKey === 'branding' && (
                    <span className="flex items-center gap-1 mr-1">
                      <span
                        className="inline-block w-3 h-3 rounded-full"
                        style={{ backgroundColor: resolveColor(data, 'primaryColor') }}
                        title={resolveColor(data, 'primaryColor')}
                      />
                      <span
                        className="inline-block w-3 h-3 rounded-full"
                        style={{ backgroundColor: resolveColor(data, 'secondaryColor') }}
                        title={resolveColor(data, 'secondaryColor')}
                      />
                    </span>
                  )}
                  <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                </div>
              </button>
            );
          })}

          {/* View History action - shows the audit trail timeline */}
          <button
            onClick={() => setCurrentStep('history')}
            className="w-full flex items-center justify-between p-2 text-left border border-gray-200 rounded-md hover:bg-primary/5 hover:border-primary/30 transition-colors"
          >
            <div className="flex items-center space-x-2">
              <ClockIcon className="h-4 w-4 text-primary" />
              <div>
                <p className="text-xs font-semibold text-gray-900">View History</p>
                <p className="text-xs text-gray-500">See recent configuration changes</p>
              </div>
            </div>
            <ChevronRightIcon className="h-4 w-4 text-gray-400" />
          </button>
        </div>
      </div>
    </div>
  );

  // ─── Render: Edit form (generic for any section) ──────────────────────

  const renderEditForm = () => {
    if (!activeSection) return null;
    const section = SECTIONS[activeSection];

    return (
      <div className="space-y-3">
        <button
          onClick={goBackToSummary}
          className="text-xs text-primary hover:text-primary/80 font-semibold"
        >
          &larr; Back to Overview
        </button>

        <h3 className="text-base font-semibold text-gray-900">
          Edit {section.label}
        </h3>
        <p className="text-xs text-gray-500">{section.description}</p>

        <div className="space-y-3">
          {section.fields.map((field) => (
            <div key={field.key}>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {field.label}
                {field.required && (
                  <span className="text-red-500 ml-0.5">*</span>
                )}
              </label>
              {field.type === 'color' ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={editData[field.key] || field.placeholder}
                    onChange={(e) =>
                      setEditData((prev) => ({
                        ...prev,
                        [field.key]: e.target.value,
                      }))
                    }
                    className="h-9 w-12 cursor-pointer border border-gray-300 rounded-md"
                  />
                  <input
                    type="text"
                    value={editData[field.key] || ''}
                    onChange={(e) =>
                      setEditData((prev) => ({
                        ...prev,
                        [field.key]: e.target.value,
                      }))
                    }
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder={field.placeholder}
                  />
                </div>
              ) : (
                <input
                  type="text"
                  value={editData[field.key] || ''}
                  onChange={(e) =>
                    setEditData((prev) => ({
                      ...prev,
                      [field.key]: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder={field.placeholder}
                />
              )}
            </div>
          ))}
        </div>        

        <div className="flex justify-end space-x-2 pt-2">
          <button
            onClick={goBackToSummary}
            className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={goToConfirm}
            disabled={!computeChangedFields()}
            className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
              computeChangedFields()
                ? 'bg-primary hover:bg-primary/90'
                : 'bg-gray-400 cursor-not-allowed opacity-50'
            }`}
          >
            Review Changes
          </button>
        </div>
      </div>
    );
  };

  // ─── Render: Confirmation ─────────────────────────────────────────────

  // Renders a colour swatch + hex code pair for the diff view
  const ColorDiffValue = ({ color, strikethrough }) => {
    const displayColor = color && isHexColor(color) ? color : null;
    return (
      <span className="inline-flex items-center gap-1.5">
        {displayColor && (
          <span
            className="inline-block w-4 h-4 rounded border border-gray-300 flex-shrink-0"
            style={{ backgroundColor: displayColor }}
          />
        )}
        <span className={strikethrough ? 'text-red-600 line-through' : 'text-green-700 font-medium'}>
          {color || '(empty)'}
        </span>
      </span>
    );
  };

  const renderConfirm = () => {
    const changes = computeChangedFields();
    if (!changes || !activeSection) {
      goBackToSummary();
      return null;
    }

    const section = SECTIONS[activeSection];
    // Build a lookup from field key → { label, type } for rendering
    const fieldMeta = {};
    section.fields.forEach((f) => {
      fieldMeta[f.key] = { label: f.label, type: f.type };
    });

    const original = getEffectiveOriginal(activeSection);

    return (
      <div className="space-y-3">
        <button
          onClick={goBackToEdit}
          className="text-xs text-primary hover:text-primary/80 font-semibold"
        >
          &larr; Back to Edit {section.label}
        </button>

        <h3 className="text-base font-semibold text-gray-900">
          Confirm Changes
        </h3>
        <p className="text-xs text-gray-600">
          Please review the following changes to{' '}
          <span className="font-semibold">{section.label}</span>:
        </p>

        <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-2">
          {Object.entries(changes).map(([key, newValue]) => {
            const meta = fieldMeta[key] || {};
            const isColor = meta.type === 'color';
            const origVal = (original[key] || '').toString().trim();

            return (
              <div key={key} className="text-xs">
                <span className="font-medium text-gray-700">
                  {meta.label || key}:
                </span>
                <div className="flex items-center space-x-2 mt-0.5 ml-2">
                  {isColor ? (
                    <>
                      <ColorDiffValue color={origVal} strikethrough />
                      <span className="text-gray-400">&rarr;</span>
                      <ColorDiffValue color={newValue} />
                    </>
                  ) : (
                    <>
                      <span className="text-red-600 line-through">
                        {origVal || '(empty)'}
                      </span>
                      <span className="text-gray-400">&rarr;</span>
                      <span className="text-green-700 font-medium">
                        {newValue || '(empty)'}
                      </span>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* When supportContacts are changed, let the user know the letterhead
            will also be updated automatically for document templates. */}
        {activeSection === 'supportContacts' && (
          <p className="text-xs text-gray-400 italic">
            These contact details will also be reflected in your letterhead for generated documents.
          </p>
        )}

        {submitError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-xs text-red-700">{submitError}</p>
          </div>
        )}

        <div className="flex justify-end space-x-2 pt-2">
          <button
            onClick={goBackToSummary}
            disabled={isSubmitting}
            className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors flex items-center space-x-1 ${
              isSubmitting
                ? 'bg-gray-400 cursor-not-allowed opacity-50'
                : 'bg-primary hover:bg-primary/90'
            }`}
          >
            {isSubmitting && (
              <svg
                className="animate-spin h-3 w-3 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            <span>{isSubmitting ? 'Saving...' : 'Confirm Changes'}</span>
          </button>
        </div>
      </div>
    );
  };

  // ─── Render: Success ──────────────────────────────────────────────────

  const renderSuccess = () => (
    <div className="space-y-3 text-center">
      <div className="flex justify-center">
        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircleSolid className="h-6 w-6 text-green-600" />
        </div>
      </div>
      <h3 className="text-base font-semibold text-gray-900">
        Settings Updated
      </h3>
      <p className="text-xs text-gray-600">
        Your organisation&apos;s {SECTIONS[activeSection]?.label || 'configuration'} has
        been updated successfully.
      </p>
      <button
        onClick={onClose}
        className="px-4 py-2 text-xs font-semibold text-white bg-primary rounded-md hover:bg-primary/90 transition-colors"
      >
        Done
      </button>
    </div>
  );

  // ─── Step router ──────────────────────────────────────────────────────

  const renderCurrentStep = () => {
    if (currentStep === 'summary') return renderSummary();
    if (currentStep === 'confirm') return renderConfirm();
    if (currentStep === 'success') return renderSuccess();
    if (currentStep === 'history') {
      return (
        <ConfigHistoryTimeline
          sections={HISTORY_SECTIONS}
          onBack={goBackToSummary}
          title="Organisation Details - Change History"
          onContentChange={measureAndAnimate}
        />
      );
    }
    if (currentStep.startsWith('edit-')) return renderEditForm();
    return renderSummary();
  };

  // ─── Modal wrapper ────────────────────────────────────────────────────

  return (
    <AlertDialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
        <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg max-w-lg w-[calc(100vw-1rem)] max-h-[95vh] overflow-y-auto z-50">
          <VisuallyHidden.Root>
            <AlertDialog.Title>Organisation Configuration</AlertDialog.Title>
          </VisuallyHidden.Root>
          <VisuallyHidden.Root>
            <AlertDialog.Description>
              Manage your organisation&apos;s support contacts, letterhead, and branding settings.
            </AlertDialog.Description>
          </VisuallyHidden.Root>

          <button
            onClick={onClose}
            className="absolute top-2 right-2 p-1 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors z-10"
            aria-label="Close"
          >
            <XIcon className="h-5 w-5" />
          </button>

          {/* Animated height wrapper - smoothly transitions the container
              height when the content changes between steps */}
          <div
            className="overflow-hidden transition-[height] duration-300 ease-in-out"
            style={{ height: animatedHeight }}
          >
            <div ref={contentRef} className="p-4 md:p-6">
              {renderCurrentStep()}
            </div>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

export default ManageOrgConfigDialog;
